import { useEffect } from "react";
import { Link } from "react-router";
import { styled } from "styled-components";

import { useFollowTopicMutation } from "api/topicsApi.slice";
import { Image, RightArrow, SectionTitleUnderlined, UIButton, UIFollowButton } from "common/components";
import { lineTruncationStyle } from "common/styles";
import { useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { fetchSuggestions } from "domains/monitor/suggestions/actions";
import {
  createSuggestedTopicsSelector,
  getIsSuggestionsPending,
  getSuggestedTopicsIds,
} from "domains/monitor/suggestions/selectors";

const StyledContainer = styled.div`
  margin-top: 2rem;
`;

const StyledTopic = styled.div`
  margin: 1rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledTitle = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: bold;
  ${lineTruncationStyle(2)};
`;

const StyledImage = styled(Image)`
  width: 64px;
  height: 64px;
  border-radius: 4px;
  margin-right: 16px;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
`;

const StyledRightArrow = styled(RightArrow)`
  width: 14px;
  height: 14px;
  margin-left: 10px;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

export const TopicSuggestions = () => {
  const dispatch = useDispatch();
  const suggestions = useMemoisedSelector(getSuggestedTopicsIds, createSuggestedTopicsSelector);
  const isPending = useSelector(getIsSuggestionsPending);
  const translate = useTranslate();

  useEffect(() => {
    dispatch(fetchSuggestions());
  }, [dispatch]);

  if (isPending || suggestions.length === 0) return null;

  return (
    <StyledContainer>
      <SectionTitleUnderlined rightElement={<SeeMoreButton />}>
        {translate("app.monitor.suggested_topics.title")}
      </SectionTitleUnderlined>
      {suggestions.map((topic) => (
        <TopicSuggestion key={topic.id} id={topic.id} name={topic.name} imageUrl={topic.images?.thumbnail} />
      ))}
    </StyledContainer>
  );
};

type TopicSuggestionProps = {
  id: string;
  name: string;
  imageUrl?: string;
};

const TopicSuggestion = ({ id, name, imageUrl }: TopicSuggestionProps) => {
  const track = useTrack();
  const [followTopic, { isLoading: isFollowingLoading }] = useFollowTopicMutation();

  const followTopicHandle = (id: string) => async () => {
    track({
      entityId: id,
      entityName: name,
      entityType: "Topic",
      name: "Follow",
      page: "Monitor",
    });
    await followTopic({ id });
  };

  return (
    <StyledTopic>
      <StyledRow>
        <StyledImage src={imageUrl} />
        <StyledTitle>{name}</StyledTitle>
      </StyledRow>
      <UIFollowButton
        variant="collapsed"
        isFollowing={false}
        onClick={followTopicHandle(id)}
        isLoading={isFollowingLoading}
        title={`Follow ${name} topic`}
      />
    </StyledTopic>
  );
};

const SeeMoreButton = () => {
  const translate = useTranslate();

  return (
    <StyledLink to="/topics">
      <UIButton variant="neutral">
        {translate("app.monitor.see_more_topics")}
        <StyledRightArrow />
      </UIButton>
    </StyledLink>
  );
};
